import { z } from 'zod';

/*
 * Licença assinada pela Cluster (Ed25519). O serviço confere a assinatura com uma chave pública
 * embutida e, a partir da v1, faz check-in periódico no back office. No check-in sai só
 * versão, número de usuários ativos e estado de saúde: nenhum dado, nome ou e-mail.
 */

/** Recursos liberados por contrato. A edição e o limite de usuários vêm à parte. */
export const FeatureSchema = z.enum(['painel', 'query_metric', 'multi_fonte', 'qlik_cloud', 'powerbi', 'siem', 'teams_bot']);
export type Feature = z.infer<typeof FeatureSchema>;

export const LicensePayloadSchema = z.object({
  id: z.string().min(1).max(64),
  cliente: z.string().min(1).max(200),
  edicao: z.enum(['selfhosted', 'cloud']),
  max_usuarios: z.number().int().min(1),
  emitida_em: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validade: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  recursos: z.array(FeatureSchema).default([]),
});
export type LicensePayload = z.infer<typeof LicensePayloadSchema>;

export const LicenseFileSchema = z.object({
  formato: z.literal(1),
  licenca: LicensePayloadSchema,
  /** Assinatura Ed25519 (base64) do JSON canônico de `licenca`. */
  assinatura: z.string().regex(/^[A-Za-z0-9+/=]{80,100}$/),
  /** Impressão da chave que assinou (para trocar a chave sem invalidar licenças antigas). */
  chave: z.string().regex(/^[0-9a-f]{16}$/),
});
export type LicenseFile = z.infer<typeof LicenseFileSchema>;

export const CheckinRequestSchema = z.object({
  license_id: z.string(),
  version: z.string(),
  instance_id: z.string(),
  active_users_30d: z.number().int().min(0),
  health: z.enum(['ok', 'degradado']),
});
export type CheckinRequest = z.infer<typeof CheckinRequestSchema>;

export const CheckinResponseSchema = z.object({
  status: z.enum(['ok', 'revogada']),
  latest_version: z.string().nullable().default(null),
  message: z.string().nullable().default(null),
  /** Id da licença e instante da resposta: amarram a assinatura a este check-in. */
  license_id: z.string().optional(),
  signed_at: z.string().optional(),
  /**
   * Assinatura Ed25519 (base64) do JSON canônico de { license_id, status, latest_version, signed_at },
   * com a mesma chave das licenças. Sem assinatura válida, o serviço trata como check-in que falhou.
   */
  signature: z.string().optional(),
});
export type CheckinResponse = z.infer<typeof CheckinResponseSchema>;

/** Estado da licença no serviço: o que o painel e o doctor mostram. */
export const LicenseStateSchema = z.object({
  status: z.enum(['valida', 'tolerancia', 'vencida', 'ausente', 'invalida']),
  cliente: z.string().nullable(),
  validade: z.string().nullable(),
  recursos: z.array(FeatureSchema),
  last_checkin_at: z.string().nullable(),
  detail: z.string(),
});
export type LicenseState = z.infer<typeof LicenseStateSchema>;
